"use client";

import { useEffect, useState, type RefObject } from "react";
import { useTranslations } from "next-intl";
import { Box, Button, Typography, useMediaQuery, useTheme } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { RADIUS } from "src/themes/tokens";

/**
 * Whether the bar is needed: only while the page's real "add to cart" button
 * is still below the bottom edge of the screen. Once it has scrolled into
 * view, or past it, the bar would only cover the button it points to.
 */
export function continueBarVisible(actionTop: number | null, viewportHeight: number): boolean {
  if (actionTop == null) return false;
  return actionTop > viewportHeight;
}

/**
 * On a phone the product pages (povečave, an artwork, an art set) open on the
 * picture, which fills the screen; the material, the size and the button sit
 * underneath with nothing saying they are there. People took the view tabs
 * ("Na steni" / "Prilagodi izrez") for the way forward. This bar says there is
 * more below and takes them to the choices. It scrolls to the options rather
 * than adding to the cart itself, so nobody buys the preselected size without
 * having seen the size picker.
 */
export function MobileContinueBar({
  targetRef,
  actionRef,
  label,
  price,
}: {
  /** The first choice the customer makes (material or size). */
  targetRef: RefObject<HTMLElement | null>;
  /** The page's own add-to-cart button; the bar leaves when it is on screen. */
  actionRef: RefObject<HTMLElement | null>;
  /** The current choice in a few words (the size), shown above the price. */
  label?: string | null;
  /** Formatted price of the current choice, or null while it is unknown. */
  price: string | null;
}) {
  const t = useTranslations("editor");
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down("md"));
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!phone) return;
    let frame = 0;
    const measure = () => {
      frame = 0;
      const el = actionRef.current;
      setVisible(continueBarVisible(el ? el.getBoundingClientRect().top : null, window.innerHeight));
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(measure);
    };
    schedule();
    // The options load after the page (variants are fetched), so the button
    // may appear a moment later; a short interval catches that and layout
    // shifts that fire no scroll event.
    const timer = window.setInterval(schedule, 500);
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [phone, actionRef]);

  if (!phone || !visible) return null;

  const goToOptions = () => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    targetRef.current?.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
  };

  return (
    <Box
      data-chat-avoid=""
      sx={(theme) => ({
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: theme.zIndex.appBar,
        px: 2,
        pt: 1.5,
        pb: "calc(12px + env(safe-area-inset-bottom))",
        bgcolor: theme.palette.background.paper,
        borderTop: `1px solid ${theme.palette.divider}`,
        boxShadow: "0 -6px 20px rgba(23, 33, 43, 0.08)",
        display: "flex",
        alignItems: "center",
        gap: 2,
      })}
    >
      {price && (
        <Box sx={{ minWidth: 0 }}>
          {label && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
              {label}
            </Typography>
          )}
          <Typography sx={{ fontSize: 20, fontWeight: 800, lineHeight: 1.2 }}>{price}</Typography>
        </Box>
      )}
      <Button
        variant="contained"
        disableElevation
        onClick={goToOptions}
        endIcon={
          <KeyboardArrowDownIcon
            sx={{
              // A few nudges downward when the bar appears: the arrow says
              // "this way", then holds still.
              "@keyframes fdNudgeDown": {
                "0%, 100%": { transform: "translateY(0)" },
                "50%": { transform: "translateY(4px)" },
              },
              animation: "fdNudgeDown 0.9s ease-in-out 0.4s 3",
              "@media (prefers-reduced-motion: reduce)": { animation: "none" },
            }}
          />
        }
        sx={(theme) => ({
          flex: 1,
          minHeight: 52,
          fontSize: 17,
          fontWeight: 800,
          textTransform: "none",
          borderRadius: `${RADIUS.card}px`,
          bgcolor: theme.tokens.action,
          color: "#fff",
          "&:hover": { bgcolor: theme.tokens.actionHover },
        })}
      >
        {t("mobileContinue.cta")}
      </Button>
    </Box>
  );
}
